import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import { StudentDiplomaDownloadUrlQuery } from 'core/operations';
import type {
  StudentDiplomaDownloadUrlQuery as IDiplomaDownloadUrlResult,
  StudentDiplomaDownloadUrlQueryVariables,
} from 'core/graphql';

/**
 * Mints a short-TTL presigned GET link for ONE diploma, on click.
 *
 * Always `network-only`: a cached link is expired by definition, and the URL is
 * a bearer token for a personal legal document — it must never be prefetched
 * into the list or persisted in the Apollo cache.
 *
 * `loadingId` is the id of the row currently being fetched (not a global flag)
 * so a table of several diplomas only spins the button that was clicked.
 *
 * Network errors propagate as a thrown ApolloError — callers own the UX.
 */
export const useDiplomaDownloadUrl = () => {
  const client = useApolloClient();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const getDownloadUrl = useCallback(
    async (diplomaId: string): Promise<string | null> => {
      setLoadingId(diplomaId);
      try {
        const { data } = await client.query<
          IDiplomaDownloadUrlResult,
          StudentDiplomaDownloadUrlQueryVariables
        >({
          query: StudentDiplomaDownloadUrlQuery,
          variables: { diplomaId },
          fetchPolicy: 'network-only',
        });
        return data?.myDiplomaDownloadUrl?.url ?? null;
      } finally {
        setLoadingId(null);
      }
    },
    [client],
  );

  return { getDownloadUrl, loadingId };
};
